import {
  TuiMultiSelect,
  TuiSelect,
  TuiChevron,
  TuiInputChip,
} from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TuiDialogContext,
  TuiDataList,
  TuiDropdown,
  TuiButton,
  TuiTextfield,
  TuiLabel,
} from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TranslatePipe } from '@ngx-translate/core';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { CounterpartySubdivision } from '@core/types';

/** Пункт списка исполнителей; id === null — «Без исполнителя» */
export interface ContractorOption {
  id: number | null;
  name: string;
}

/** Текущие значения фильтра, передаются в диалог при открытии */
export interface ContractorFilterDialogData {
  contractorCounterpartyId?: number | (number | null)[];
  contractorCounterpartySubdivisionId?: number;
}

/** Результат диалога; пустой объект = «Сбросить» */
export interface ContractorFilterDialogResult {
  contractorCounterpartyId?: (number | null)[];
  contractorCounterpartySubdivisionId?: number;
}

/**
 * Модалка фильтра «Исполнитель» (порт #subdivisionModal из старой системы):
 * мульти-выбор исполнителей-контрагентов (вкл. «Без исполнителя») и
 * подразделение — доступно только при ровно одном выбранном исполнителе.
 */
@Component({
  selector: 'x-contractor-filter-dialog',
  imports: [
    TuiTextfield,
    TuiLabel,
    TuiChevron,
    TuiInputChip,
    FormsModule,
    TranslatePipe,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiMultiSelect,
    TuiSelect,
  ],
  templateUrl: './contractor-filter-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorFilterDialogComponent implements OnInit {
  protected readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
    ContractorFilterDialogResult,
    ContractorFilterDialogData | undefined
  >;
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly translateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  protected contractorOptions: ContractorOption[] = [];
  protected subdivisions: CounterpartySubdivision[] = [];
  protected selectedContractors: ContractorOption[] = [];
  protected selectedSubdivision: CounterpartySubdivision | null = null;
  private subdivisionRequestId = 0;

  protected readonly stringifyFn = ({ name }: { name: string }) => name;

  ngOnInit(): void {
    this.requestsApiService
      .getAvailableContractors$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(contractors => {
        this.contractorOptions = [
          {
            id: null,
            name: this.translateService.instant(
              'filters_buttons.without_contractor',
            ),
          },
          ...contractors.map(contractor => ({
            id: contractor.id,
            name: contractor.name ?? '',
          })),
        ];
        this.restoreSelectionFromData();
        this.cdr.markForCheck();
      });
  }

  /** Восстанавливает выбор из текущего фильтра (переданного при открытии) */
  private restoreSelectionFromData(): void {
    const data = this.context.data;
    const currentIds = Array.isArray(data?.contractorCounterpartyId)
      ? data.contractorCounterpartyId
      : typeof data?.contractorCounterpartyId === 'number'
        ? [data.contractorCounterpartyId]
        : [];

    this.selectedContractors = this.contractorOptions.filter(option =>
      currentIds.includes(option.id),
    );
    this.onContractorsChange(data?.contractorCounterpartySubdivisionId);
  }

  /** id единственного выбранного (не-null) исполнителя или null */
  protected getSingleContractorId(): number | null {
    return this.selectedContractors.length === 1
      ? this.selectedContractors[0].id
      : null;
  }

  /**
   * При смене исполнителей: подразделение доступно только для ровно одного
   * выбранного исполнителя (не «Без исполнителя») — как в старой системе.
   */
  protected onContractorsChange(restoreSubdivisionId?: number): void {
    const singleContractorId = this.getSingleContractorId();
    const requestId = ++this.subdivisionRequestId;

    this.selectedSubdivision = null;
    this.subdivisions = [];

    if (singleContractorId === null) {
      return;
    }

    this.requestsApiService
      .getContractorSubdivisions$(singleContractorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(subdivisions => {
        if (requestId !== this.subdivisionRequestId) {
          return;
        }

        this.subdivisions = subdivisions;
        if (restoreSubdivisionId != null) {
          this.selectedSubdivision =
            subdivisions.find(
              subdivision => subdivision.id === restoreSubdivisionId,
            ) ?? null;
        }
        this.cdr.markForCheck();
      });
  }

  protected apply(): void {
    this.context.completeWith({
      ...(this.selectedContractors.length
        ? {
            contractorCounterpartyId: this.selectedContractors.map(
              contractor => contractor.id,
            ),
          }
        : {}),
      ...(this.selectedSubdivision
        ? { contractorCounterpartySubdivisionId: this.selectedSubdivision.id }
        : {}),
    });
  }

  protected reset(): void {
    this.context.completeWith({});
  }
}
